import { lxIsNaN } from "@lxo-blocks/web";
import { Box, SkeletonProps, SxProps, Typography } from "@mui/material";
import { SpacingArgument } from "@mui/system/createTheme/createSpacing";
import React from "react";
import { useLxTheme } from "../Themes/useLxTheme";

interface LxImgProps {
  /** The source URL */
  src: string;
  /** The alt img text  */
  alt: string;
  /** Width of the image in spacing units*/
  width: SpacingArgument;
  /** Height of the image in spacing units*/
  height: SpacingArgument /**
  /** SxProps, applied to the wrapper Box */;
  sx?: SxProps;
  /** If the image could not be found, this fallback will be shown instead */
  fallback?: JSX.Element;
  /** ClassName, applied to the wrapper Box */
  className?: string;
}
/**
 * LxImg is a wrapper for the <code>img</code> tag.
 *
 * It provides:
 * - a loading placeholder
 * - a fallback image
 * - enforces a width and height
 * - enforces an alt attribute
 * - the sx prop
 *
 *
 * ❗ Note: `width` and `height` are in spacing units.
 *  If you want to use "real" pixels, you have to divide by 8, e.g. `width={200/8}`
 *
 *
 * @package Media
 * @beta
 * @example
 * import MySvg from "./my-svg.svg";
 *
 * // Basic usage
 * <LxImg src={MySvg} sx={{m:2}} alt="My Svg" width={12} height={12} />
 *
 */
export function LxImg({ src, alt, sx, fallback, width, height }: LxImgProps) {
  const [state, setState] = React.useState<"loaded" | "error">("loaded");
  const onError = () => setState("error");

  const Fallback = fallback ? () => fallback : DefaultFallBackImage;

  const theme = useLxTheme();

  const pWidth = lxIsNaN(width) ? width : theme.spacing(Number(width));
  const pHeight = lxIsNaN(height) ? height : theme.spacing(Number(height));

  return (
    <Box sx={{ position: "relative" }}>
      {state !== "error" && (
        <Box
          sx={{
            width: pWidth,
            height: pHeight,
            ...sx,
          }}
        >
          <img
            loading="lazy"
            width={pWidth}
            height={pHeight}
            src={src}
            onError={onError}
            alt={alt}
          />
        </Box>
      )}
      {state === "error" && (
        <Fallback width={pWidth} height={pHeight} alt={alt} />
      )}
    </Box>
  );
}

function DefaultFallBackImage(props: SkeletonProps & { alt: string }) {
  const theme = useLxTheme();
  return (
    <Box
      sx={{
        width: props.width,
        height: props.height,
        display: "grid",
        placeContent: "center",
        backgroundColor: theme.palette.grey["100"],
      }}
    >
      <Typography>{props.alt || "?"}</Typography>
    </Box>
  );
}
